import { Form, Input, Progress, Typography } from 'antd';
import { CheckmarkCircle02Icon, CircleIcon, LockPasswordIcon } from '@hugeicons/core-free-icons';
import { useState } from 'react';
import { Icon } from '@/components/index.js';
import { space, typography } from '@/design/tokens.js';
import { evaluatePassword } from './password-policy.js';

const { Text } = Typography;

export interface PasswordFieldProps {
  name: string;
  label: string;
  autoComplete?: string;
  autoFocus?: boolean;
  /** Shows the live rule checklist. Off for "current password" fields. */
  showRules?: boolean;
}

/**
 * A password input with a live policy checklist.
 *
 * The checklist updates as the user types rather than after they submit. That
 * is Nielsen's visibility of system status applied to the single most
 * frustrating form in any product: being told "password not strong enough",
 * fixing one thing, and being told it again for the next.
 *
 * Validation is bound to the same rules, so the submit button's error and the
 * checklist can never disagree with each other.
 */
export function PasswordField({
  name,
  label,
  autoComplete = 'new-password',
  autoFocus = false,
  showRules = true,
}: PasswordFieldProps) {
  const [value, setValue] = useState('');
  const strength = evaluatePassword(value);
  const touched = value.length > 0;

  return (
    <>
      <Form.Item
        name={name}
        label={label}
        rules={[
          { required: true, message: `Enter your ${label.toLowerCase()}` },
          ...(showRules
            ? [
                {
                  validator: (_: unknown, candidate: string) => {
                    if (!candidate) return Promise.resolve();
                    const result = evaluatePassword(candidate);
                    return result.valid
                      ? Promise.resolve()
                      : Promise.reject(new Error('This password does not meet the policy'));
                  },
                },
              ]
            : []),
        ]}
        style={{ marginBottom: showRules ? space[2] : undefined }}
      >
        <Input.Password
          size="large"
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          placeholder="••••••••"
          prefix={<Icon icon={LockPasswordIcon} size="sm" />}
          onChange={(event) => setValue(event.target.value)}
        />
      </Form.Item>

      {showRules ? (
        <div style={{ marginBottom: space[4] }}>
          <Progress
            percent={touched ? ((strength.score + 1) / 5) * 100 : 0}
            showInfo={false}
            size="small"
            strokeColor={
              strength.valid
                ? 'var(--orbit-tone-success-solid)'
                : strength.score >= 2
                  ? 'var(--orbit-tone-warning-solid)'
                  : 'var(--orbit-tone-danger-solid)'
            }
            aria-hidden
          />
          <ul
            // Announced as a group when it changes, so a screen-reader user
            // learns what is still missing without hunting for it.
            aria-live="polite"
            style={{
              listStyle: 'none',
              margin: `${space[2]}px 0 0`,
              padding: 0,
              display: 'grid',
              gap: space[1],
            }}
          >
            {strength.rules.map((rule) => (
              <li key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: space[2] }}>
                <Icon
                  icon={rule.satisfied ? CheckmarkCircle02Icon : CircleIcon}
                  size="xs"
                  color={
                    rule.satisfied
                      ? 'var(--orbit-tone-success-solid)'
                      : 'var(--ant-color-text-quaternary)'
                  }
                />
                <Text
                  type={rule.satisfied ? undefined : 'secondary'}
                  style={{ fontSize: typography.size.sm }}
                >
                  {rule.label}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}
